/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';

interface ImageLoadEvent {
  url: string;
  loadTime: number;
}

interface ImageErrorEvent {
  url: string;
  error: string;
}

@Component({
  selector: 'app-loading-spinner',
  templateUrl: './loading-spinner.component.html',
  styleUrls: ['./loading-spinner.component.scss'],
})
export class LoadingSpinnerComponent implements OnChanges, OnDestroy {

  /** Fallback contra spinner eterno: se o `<img>` não disparar load nem error em N ms,
   *  forçamos imageLoaded=true. Evita o sintoma em qualquer cenário onde o evento
   *  do browser não chega (cache + DOM reuse, navegação durante fetch, etc.). */
  private static readonly LOAD_TIMEOUT_MS = 8000;
  private loadTimeoutId?: ReturnType<typeof setTimeout>;

  @Input() click!: (...args: any[]) => any;
  @Input() index!: any; // Index do item na lista
  @Input() image: string = ""; // URL da imagem
  @Input() customStyle?: { [key: string]: string };
  /** URL alternativa (ex.: api2) tentada se a `image` falhar — contorna o bloqueio
   *  do CDN na web (ERR_BLOCKED_BY_RESPONSE.NotSameOrigin / 403 na origem primária). */
  @Input() fallbackImage?: string;
  @Input() fallback: string = "assets/imgs/no-picture.png"; // Imagem de fallback
  @Input() loadingSpinner: string = "assets/imgs/loading-spinner.gif"; // Spinner de loading
  
  // Configurações avançadas de lazy loading
  @Input() enablePreload: boolean = true;
  @Input() enableCompression: boolean = true;
  @Input() compressionQuality: number = 0.8;
  @Input() aspectRatio: string = "1:1";
  @Input() rootMargin: string = "100px";
  @Input() threshold: number = 0.1;
  @Input() enableWebP: boolean = true;
  @Input() showProgress: boolean = false;
  @Input() borderRadius: string = "8%";
  @Input() width: string = "100%";
  /** Ajuste da imagem: 'cover' (preenche, corta) ou 'contain' (foto inteira, sem corte). */
  @Input() fit: 'cover' | 'contain' = 'cover';

  @Output() imageLoadedEvent = new EventEmitter<ImageLoadEvent>();
  @Output() imageErrorEvent = new EventEmitter<ImageErrorEvent>();

  imageLoaded: boolean = false; // Estado de carregamento da imagem
  /** Src efetivo do <img>. Começa em `image` e, em erro, cai pra `fallbackImage` e depois `fallback`. */
  currentSrc: string = "";
  private triedFallbackImage = false;
  private triedFallback = false;

  constructor(){}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['image']) {
      this.imageLoaded = false;
      this.triedFallbackImage = false;
      this.triedFallback = false;
      this.currentSrc = this.image;
      this.clearLoadTimeout();
      if (this.image) {
        this.armLoadTimeout();
      }
    }
  }

  private armLoadTimeout(): void {
    this.loadTimeoutId = setTimeout(() => {
      if (!this.imageLoaded) {
        console.warn('[loading-spinner] image load timed out, forcing imageLoaded=true:', this.currentSrc);
        this.imageLoaded = true;
      }
    }, LoadingSpinnerComponent.LOAD_TIMEOUT_MS);
  }

  ngOnDestroy(): void {
    this.clearLoadTimeout();
  }

  private clearLoadTimeout(): void {
    if (this.loadTimeoutId) {
      clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = undefined;
    }
  }

  onImageLoad(_event: any): void {
    this.clearLoadTimeout();
    this.imageLoaded = true;
    const loadTime = performance.now();
    this.imageLoadedEvent.emit({ url: this.image, loadTime });
  }

  onImageError(_event: any): void {
    this.clearLoadTimeout();

    // 1) tenta a origem alternativa (ex.: api2) antes de desistir — contorna CORP/403 do CDN.
    if (this.fallbackImage && !this.triedFallbackImage && this.currentSrc !== this.fallbackImage) {
      this.triedFallbackImage = true;
      this.imageLoaded = false;
      this.currentSrc = this.fallbackImage;
      this.armLoadTimeout();
      return;
    }

    // 2) por fim, imagem local de fallback (no-picture) em vez de <img> quebrada.
    if (this.fallback && !this.triedFallback && this.currentSrc !== this.fallback) {
      this.triedFallback = true;
      this.imageLoaded = false;
      this.currentSrc = this.fallback;
      this.armLoadTimeout();
      return;
    }

    this.imageLoaded = true;
    this.imageErrorEvent.emit({ url: this.image, error: 'Image failed to load' });
  }

  handleClick(): void {
    if (this.click) {
      this.click(this.index); // Chama a função se ela foi fornecida
    }
  }
}